import _ from 'lodash'
import {Extension, PointerMethods, DmApis, pointerUtils} from '@wix/document-manager-core'
import type {Pointer} from '@wix/document-services-types'
import type {RelationshipsAPI} from './relationships'
import {MASTER_PAGE_ID, VIEW_MODES, DATA_TYPES} from '../constants/constants'
const {getPointer, getRepeatedItemPointerIfNeeded} = pointerUtils

const createPointersMethods = ({pointers, dal, extensionAPI}: DmApis): PointerMethods => {
    const {relationships} = extensionAPI as RelationshipsAPI
    const {siteAPI} = extensionAPI as any
    const controllerIdCache = {}

    const getControllerRefFromId = (
        controllerDataId: string,
        pagePointer: Pointer,
        viewMode: string
    ): Pointer | null => {
        const dataPointer = pointers.data.getDataItem(controllerDataId, pagePointer.id)
        let owningReferences = relationships.getOwningReferencesToPointer(dataPointer, DATA_TYPES.controllers)
        // in case of scoped controllers we are using the data item itself as the controller ref
        if (owningReferences.length > 0) {
            return dataPointer
        }

        owningReferences = relationships.getOwningReferencesToPointer(dataPointer, viewMode)

        if (owningReferences.length === 0 && viewMode === VIEW_MODES.MOBILE) {
            owningReferences = relationships.getOwningReferencesToPointer(dataPointer, VIEW_MODES.DESKTOP)
        }
        if (owningReferences.length > 0) {
            const component = owningReferences[0]
            const pageOfComponent = pointers.structure.getPageOfComponent(component)

            if (pageOfComponent.id === pagePointer.id) {
                return component
            }

            if (pointers.structure.isMasterPage(pagePointer)) {
                return null
            }
            if (pageOfComponent.id === MASTER_PAGE_ID) {
                return component
            }
        }

        // getCompIdByDataQueryId is only implemented in TB
        const componentId =
            _.has(siteAPI, ['getCompIdByDataQueryId']) &&
            siteAPI.getCompIdByDataQueryId(controllerDataId, pagePointer.id)
        if (componentId) {
            return getPointer(componentId, viewMode)
        }

        return null
    }

    const getControllerInPageByDataId = (
        pagePointer: Pointer,
        isMobileView: boolean,
        controllerDataId: string
    ): Pointer | null => {
        if (!controllerDataId) {
            return null
        }
        const cacheKey = `${pagePointer.id}-${isMobileView}-${controllerDataId}`
        const cached = controllerIdCache[cacheKey]
        if (cached && dal.has(getRepeatedItemPointerIfNeeded(cached))) {
            return cached
        }

        const controller = getControllerRefFromId(
            controllerDataId,
            pagePointer,
            isMobileView ? VIEW_MODES.MOBILE : VIEW_MODES.DESKTOP
        )
        if (controller) {
            controllerIdCache[cacheKey] = controller
            return controller
        }
        return null
    }

    return {
        platform: {
            getControllerInPageByDataId
        }
    }
}

const createExtension = (): Extension => ({
    name: 'platformWithViewer',
    dependencies: new Set(['relationships', 'structure', 'page', 'fullToDisplay']),
    createPointersMethods
})

export {createExtension}
